import React from 'react';
import {
  Box,
  Checkbox,
  Chip,
  Collapse,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  IRatingFormQuestion,
  RatingQuestionType,
} from '../../Interfaces/IRatingForm';
import { QuestionPreview } from './QuestionPreview';
import {
  MAX_SCALE_STEPS,
  MIN_SCALE_STEPS,
  flagFromBackend,
  questionSummary,
  scaleMaxOnBlur,
  scaleMaxWhileTyping,
} from './ratingFormUtils';

const QUESTION_TYPE_LABEL: Record<RatingQuestionType, string> = {
  stars: 'Sterne',
  scale: 'Skala',
  yesno: 'Ja / Nein',
  text: 'Freitext',
};

export interface IQuestionRowProps {
  question: IRatingFormQuestion;
  expanded: boolean;
  isFirst: boolean;
  isLast: boolean;
  onToggle: () => void;
  onChange: (patch: Partial<IRatingFormQuestion>) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
  /** Stabile Identität für den Sortier-Kontext (= _clientKey). */
  sortableId: string;
}

export const QuestionRow: React.FC<IQuestionRowProps> = ({
  question: q,
  expanded,
  isFirst,
  isLast,
  onToggle,
  onChange,
  onMove,
  onRemove,
  sortableId,
}) => {
  const summary = questionSummary(q);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableId });

  return (
    <Box
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
      }}
      sx={{
        border: '1px solid',
        borderColor: expanded ? 'primary.main' : 'divider',
        borderRadius: 1,
        mb: 1,
        // Pfeile erst beim Ueberfahren/Fokussieren voll sichtbar, damit die
        // Zeile ruhig bleibt — nie ausgeblendet, sonst nicht auffindbar.
        '&:hover .row-actions, &:focus-within .row-actions': { opacity: 1 },
      }}
    >
      <Stack
        direction='row'
        alignItems='center'
        spacing={1}
        sx={{ px: 1, py: 0.75, cursor: 'pointer' }}
        onClick={onToggle}
      >
        <Box
          {...attributes}
          {...listeners}
          onClick={e => e.stopPropagation()}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'grab',
            color: 'text.disabled',
            '&:active': { cursor: 'grabbing' },
          }}
        >
          <DragIndicatorIcon fontSize='small' />
        </Box>

        <Typography
          variant='body2'
          sx={{
            flex: 1,
            fontStyle: summary.labelMissing ? 'italic' : 'normal',
            color: summary.labelMissing ? 'text.secondary' : 'text.primary',
          }}
        >
          {summary.labelMissing ? 'Neue Frage' : q.Label}
        </Typography>

        {summary.labelMissing && (
          <Chip
            size='small'
            color='warning'
            icon={<WarningAmberIcon fontSize='small' />}
            // „noch nicht": ohne das „noch" liest es sich wie ein Fehler, und
            // man bricht die Eingabe ab, statt eine Bezeichnung zu ergänzen
            // (QS #9, TF12).
            label='wird noch nicht gespeichert'
          />
        )}
        <Chip size='small' variant='outlined' label={summary.typeChip} />
        {summary.required && <Chip size='small' label='Pflicht' />}
        {summary.excludedFromOverall && (
          <Chip size='small' variant='outlined' label='zählt nicht' />
        )}

        <Stack
          direction='row'
          className='row-actions'
          sx={{ opacity: 0.25, transition: 'opacity .15s' }}
          onClick={e => e.stopPropagation()}
        >
          <Tooltip title='Nach oben'>
            <span>
              <IconButton
                size='small'
                disabled={isFirst}
                onClick={() => onMove(-1)}
              >
                <ArrowUpwardIcon fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title='Nach unten'>
            <span>
              <IconButton
                size='small'
                disabled={isLast}
                onClick={() => onMove(1)}
              >
                <ArrowDownwardIcon fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        <IconButton
          size='small'
          aria-expanded={expanded}
          aria-label={expanded ? 'Frage zuklappen' : 'Frage aufklappen'}
          onClick={e => {
            e.stopPropagation();
            onToggle();
          }}
        >
          <ExpandMoreIcon
            fontSize='small'
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'none',
              transition: 'transform .15s',
              opacity: 0.6,
            }}
          />
        </IconButton>
      </Stack>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ px: 2, pb: 2, pt: 0.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              <TextField
                label='Bezeichnung'
                fullWidth
                size='small'
                autoFocus
                value={q.Label}
                onChange={e => onChange({ Label: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size='small'>
                <InputLabel>Typ</InputLabel>
                <Select
                  label='Typ'
                  value={q.QuestionType}
                  onChange={e => {
                    const type = e.target.value as RatingQuestionType;
                    const qp: Partial<IRatingFormQuestion> = {
                      QuestionType: type,
                    };
                    if (type === 'stars') {
                      qp.RangeMin = 1;
                      qp.RangeMax = 5;
                      qp.CountsForOverall = true;
                    } else if (type === 'scale') {
                      qp.RangeMin = 1;
                      qp.RangeMax = 10;
                      qp.CountsForOverall = true;
                    } else if (type === 'yesno') {
                      qp.RangeMin = 0;
                      qp.RangeMax = 1;
                      qp.CountsForOverall = false;
                    } else {
                      qp.RangeMin = null;
                      qp.RangeMax = null;
                      qp.CountsForOverall = false;
                    }
                    onChange(qp);
                  }}
                >
                  {(['stars', 'scale', 'yesno', 'text'] as const).map(t => (
                    <MenuItem key={t} value={t}>
                      {QUESTION_TYPE_LABEL[t]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Kein Min-Feld: Sterne und Skala beginnen immer bei 1.
                Ein Bogen, der bei 3 Sternen anfaengt, ergibt fachlich
                keinen Sinn — das Backend setzt die 1 beim Speichern. */}
            {(q.QuestionType === 'stars' || q.QuestionType === 'scale') && (
              <Grid item xs={6} md={2}>
                <TextField
                  label={
                    q.QuestionType === 'stars' ? 'Anzahl Sterne' : 'Skala bis'
                  }
                  type='number'
                  fullWidth
                  size='small'
                  inputProps={{
                    min: MIN_SCALE_STEPS,
                    max: MAX_SCALE_STEPS,
                    step: 1,
                  }}
                  helperText={
                    q.QuestionType === 'stars'
                      ? `${MIN_SCALE_STEPS} bis ${MAX_SCALE_STEPS} Sterne`
                      : `Beginnt bei 1, endet bei ${MIN_SCALE_STEPS}–${MAX_SCALE_STEPS}`
                  }
                  value={q.RangeMax ?? ''}
                  onChange={e =>
                    onChange({
                      RangeMin: 1,
                      RangeMax: scaleMaxWhileTyping(e.target.value),
                    })
                  }
                  // Erst beim Verlassen auf den gueltigen Bereich
                  // einrasten — waehrend des Tippens wuerde das die
                  // Eingabe von "10" unmoeglich machen.
                  onBlur={() =>
                    onChange({
                      RangeMin: 1,
                      RangeMax: scaleMaxOnBlur(
                        q.RangeMax,
                        q.QuestionType === 'stars' ? 5 : MAX_SCALE_STEPS
                      ),
                    })
                  }
                />
              </Grid>
            )}

            <Grid item xs={6} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    size='small'
                    checked={flagFromBackend(q.IsRequired, true)}
                    onChange={(_, v) => onChange({ IsRequired: v })}
                  />
                }
                label='Pflichtfeld'
              />
            </Grid>
            <Grid item xs={6} md={5}>
              <FormControlLabel
                control={
                  <Checkbox
                    size='small'
                    checked={flagFromBackend(q.CountsForOverall, true)}
                    disabled={
                      q.QuestionType === 'text' || q.QuestionType === 'yesno'
                    }
                    onChange={(_, v) => onChange({ CountsForOverall: v })}
                  />
                }
                label='In Gesamtwert einrechnen'
              />
            </Grid>

            {/* Mandanten-nahe Vorschau, damit der Edit-Bereich dem
                fertigen Bogen aehnelt (#1009). */}
            <Grid item xs={12}>
              <Box
                sx={{
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  px: 2,
                  py: 1.5,
                }}
              >
                <Typography variant='caption' color='text.secondary'>
                  Vorschau
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <QuestionPreview q={q} />
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Stack direction='row' justifyContent='flex-end'>
                <IconButton size='small' color='error' onClick={onRemove}>
                  <DeleteOutlineIcon fontSize='small' />
                  <Typography variant='body2' sx={{ ml: 0.5 }}>
                    Frage entfernen
                  </Typography>
                </IconButton>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Box>
  );
};
